import { Dialog as MUIDialog, DialogContent } from '@material-ui/core'
import React, { ReactElement, useContext } from 'react'
import { ModalContext, ModalContextOutputType } from '../../Contexts/ModalContext'
import Icon, { IconName } from '../Icon'
import InlinePrompt, { InlinePromptProps } from '../InlinePrompt/InlinePrompt'
import ModalExitPromptContext from './ModalExitPromptContext'

/**
 * Component properties type.
 */
type Props = {
    classNames?: string
    children: ReactElement | ReactElement[]
    isOpen: boolean
    onClose?: () => void
    onOpen?: () => void
    closeOnBackdropClick?: boolean
    isClosableWithXButton?: boolean
    inlineExitPromptSettings?: InlinePromptProps
    onExited?: () => void
    isViewOnly?: boolean
}

/**
 * Dialog (Modal implementation) component.
 *
 * @param classNames a set of classes to add to the modal
 * @param children Modal window content.
 * @param isOpen Whether the modal window is open.
 * @param onClose Callback on close.
 * @param onOpen Callback on open.
 * @param closeOnBackdropClick Whether the click on modal window backdrop should close the modal window.
 * @param isClosableWithXButton Whether it is possible to close with the 'X' button displayed in top right corner.
 * @param inlineExitPromptSettings Properties for prompt on modal close.
 * @param onExited Callback when the modal is closed. Designed to be used when the modal is interacting with DOM
 * @param isViewOnly View only flag
 * directly and it is necessary to do the clean-up after the modal is closed.
 */
const Dialog: React.FC<Props> = ({
    classNames,
    inlineExitPromptSettings,
    onClose,
    isOpen,
    onOpen,
    closeOnBackdropClick,
    onExited,
    isClosableWithXButton,
    isViewOnly,
    children
}: Props): ReactElement => {
    const { displayExitPrompt, closeExitPrompt, isExitPromptDisplayed } = useContext(ModalExitPromptContext)
    const { popoutMode, popoutFunc, disablePopoutOption }: ModalContextOutputType = useContext(ModalContext)

    /**
     * Handles close action.
     */
    const handleClose = (): void => {
        if (inlineExitPromptSettings && !isViewOnly) {
            displayExitPrompt()
        } else if (onClose) {
            onClose()
        }
    }

    /**
     * Handles close confirmation when prompt is enabled.
     */
    const confirmPromptClose = (): void => {
        if (onClose) {
            onClose()
        }
        setTimeout(() => {
            closeExitPrompt()
        }, 1000)
    }

    /**
     * Handles close cancelling when prompt is enabled.
     */
    const cancelPromptClose = (): void => {
        closeExitPrompt()
    }

    let contentWrapperClass
    if (isExitPromptDisplayed) {
        contentWrapperClass = 'hidden-content'
    } else {
        contentWrapperClass = `modal-content-wrapper${isClosableWithXButton ? '-closable' : ''}`
    }

    const promptClass = `${isExitPromptDisplayed ? 'exit-prompt-wrapper' : 'hidden-content'}`

    const closeOnEscape = (event?: KeyboardEvent): void => {
        if (event && !event.defaultPrevented && event.key === 'Escape') {
            handleClose()
        }
    }

    return (
        <MUIDialog
            className={
                classNames
                    ? `modal ${classNames} ${popoutMode ? 'popout-mode' : ''}`
                    : `modal ${popoutMode ? 'popout-mode' : ''}`
            }
            open={isOpen}
            onClose={(_: Event, reason: string): void => {
                if ((!closeOnBackdropClick || isExitPromptDisplayed) && reason === 'backdropClick') {
                    return undefined
                }

                return handleClose()
            }}
            maxWidth={false}
            disableEscapeKeyDown
            disableEnforceFocus
            TransitionProps={{
                onEntered: onOpen,
                onExited
            }}
            onKeyDown={(e: React.KeyboardEvent): void => closeOnEscape(e.nativeEvent)}
        >
            <DialogContent>
                {(isClosableWithXButton || !!popoutFunc) && !isExitPromptDisplayed && (
                    <div className='modal-header'>
                        {isClosableWithXButton && (
                            <div className='button-x' onClick={handleClose}>
                                <Icon iconName={IconName.X} />
                            </div>
                        )}
                        {!popoutMode && popoutFunc && (
                            <div
                                className={`button-popout ${disablePopoutOption ? 'disabled' : ''}`}
                                onClick={(): void => {
                                    if (!disablePopoutOption) {
                                        popoutFunc()
                                    }
                                }}
                            >
                                <Icon iconName={IconName.OPEN_IN_NEW} />
                            </div>
                        )}
                    </div>
                )}
                <div className={contentWrapperClass}>{children}</div>
                {/* Close confirmation prompt. */}
                {!!inlineExitPromptSettings && (
                    <div className={promptClass}>
                        <InlinePrompt
                            onConfirm={confirmPromptClose}
                            onCancel={cancelPromptClose}
                            {...inlineExitPromptSettings}
                        />
                    </div>
                )}
            </DialogContent>
        </MUIDialog>
    )
}

export default Dialog
